import { EventTrackingAction } from '../declarations';
import React from 'react';
import { useObservability } from './useObservability';

type WithEventTrackingProps<InnerProps> = InnerProps & {
  /**
   * The name param to be used in trackEvent call, default to 'click'.
   */
  eventName?: EventTrackingAction;
  /**
   * The label param to be used in trackEvent call.
   */
  eventLabel?: string;
  /**
   * The data param to be used in trackEvent call.
   */
  eventData?: { [k: string]: unknown };
};

/**
 * the withOnClickEventTracking HOC takes a React Component and returns a new React Component that adds event props to the original component,
 * and add observability trackEvent call in the onClick handler.
 *
 * Example 1: a component with no customized onClick handler:
 * const Div: React.FC = ({children}) => <div>{children}</div>;
 * const DivWithEventTracking = withOnClickEventTracking(Div);
 *
 * You can use DivWithEventTracking just like how you would use <Div>,but optionally with event props:
 * <DivWithEventTracking eventLabel="div-test" />;
 *
 * Example 2: a component with customized onClick handler returns void:
 * const Button: React.FC<{ onClick: () => void }> = ({ onClick, children }) => (
 *   <button onClick={onClick}>{children}</button>
 * );
 * const ButtonWithEventTracking = withOnClickEventTracking(Button);
 *
 * You can use ButtonWithEventTracking just like how you use <Button>, but optionally with event props:
 * <ButtonWithEventTracking onClick={() => console.log('onClick')} eventName="click-browse" />;
 *
 * Example 3: a component with customized onClick handler and other props:
 * const Link: React.FC<{
 *   onClick: (e: React.MouseEvent<HTMLElement>) => Promise<string>;
 *   link: string;
 * }> = ({ onClick, link, children }) => (
 *   <a onClick={onClick} href={link}>
 *     {children}
 *   </a>
 * );
 * const LinkWithEventTracking = withOnClickEventTracking(Link);
 *
 * You can use
 * <LinkWithEventTracking eventData={{source: 'observability'}} link="#" onClick={() => Promise.resolve('link')}>
 *   link
 * </LinkWithEventTracking>;
 */
export function withOnClickEventTracking<Props extends { onClick?: Function }>(
  InnerComponent: React.ComponentType<Props>,
): React.ComponentType<WithEventTrackingProps<Props>> {
  const NewComponent: React.FC<WithEventTrackingProps<Props>> = ({
    eventData,
    eventLabel,
    eventName = 'click',
    onClick,
    ...props
  }) => {
    const { trackEvent } = useObservability();
    const clickHandler: Props['onClick'] = (...args: any[]) => {
      trackEvent(eventName, eventLabel, eventData);
      if (!onClick) {
        return;
      }
      return onClick(...args);
    };

    // @ts-ignore to surpress TS error:
    // 'Omit<PropsWithChildren<WithEventTrackingProps<Props>>, "eventData" | "eventLabel" | "eventName" | "onClick"> & { ...; }'
    // is assignable to the constraint of type 'Props',
    // but 'Props' could be instantiated with a different subtype of constraint '{ onClick?: Function | undefined; }'
    return <InnerComponent {...props} onClick={clickHandler} />;
  };
  return NewComponent;
}
